/**
 * DrawerErrorBoundary
 *
 * Error boundary for catching render errors in drawer content.
 * Provides a user-friendly error state with retry option.
 *
 * @see docs/stories/DRAWER-5.5-final-polish-edge-cases.md
 */

import { Component, ErrorInfo, ReactNode } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackDrawerError } from "@/utils/drawerAnalytics";
import type { DrawerType } from "@/types/drawer";

// ============================================================================
// Types
// ============================================================================

interface DrawerErrorBoundaryProps {
  children: ReactNode;
  drawerType: DrawerType;
  onRetry?: () => void;
}

interface DrawerErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

// ============================================================================
// Component
// ============================================================================

export class DrawerErrorBoundary extends Component<
  DrawerErrorBoundaryProps,
  DrawerErrorBoundaryState
> {
  constructor(props: DrawerErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): DrawerErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[DrawerErrorBoundary] Caught error:", error, errorInfo);

    // Track error in analytics
    trackDrawerError({
      drawerType: this.props.drawerType,
      error: error.message,
      context: {
        componentStack: errorInfo.componentStack,
      },
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
    this.props.onRetry?.();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full min-h-[300px] p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-destructive mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">
            Something went wrong
          </h3>
          <p className="text-sm text-muted-foreground mb-6 max-w-xs">
            We couldn't load this content. Please try again or close the drawer.
          </p>
          <Button onClick={this.handleRetry} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Try Again
          </Button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default DrawerErrorBoundary;
